/**
 * Browser capability checks for the unified recorder.
 *
 * The recording pipeline has two paths:
 *   - Streaming (WebM + MediaRecorder timeslice chunking → assembly CF →
 *     transcode CF). Works on Chromium-based browsers.
 *   - Legacy single-blob upload (record to memory, upload on session
 *     finalize). Works anywhere MediaRecorder produces a playable blob.
 *
 * iOS Safari / WebKit can't produce WebM — it only emits MP4, which can't
 * be naively concatenated from MediaRecorder chunks. So on iOS we route
 * video through the legacy path just like we already do for audio.
 *
 * If no video mime type is supported at all (very old or locked-down
 * browsers), the Video toggle is disabled entirely.
 */

function isTypeSupported(mimeType) {
  try {
    return typeof MediaRecorder !== 'undefined' &&
      typeof MediaRecorder.isTypeSupported === 'function' &&
      MediaRecorder.isTypeSupported(mimeType);
  } catch (e) {
    return false;
  }
}

// Any video mime type the browser will let us record.
export function canRecordVideo() {
  if (typeof MediaRecorder === 'undefined') return false;
  return (
    isTypeSupported('video/webm;codecs=vp9,opus') ||
    isTypeSupported('video/webm;codecs=vp8,opus') ||
    isTypeSupported('video/webm;codecs=h264,opus') ||
    isTypeSupported('video/webm') ||
    isTypeSupported('video/mp4')
  );
}

// Can we use the streaming pipeline? Requires a WebM container because
// only WebM chunks from MediaRecorder concatenate into a playable file.
export function canStreamWebmVideo() {
  return (
    isTypeSupported('video/webm;codecs=vp9,opus') ||
    isTypeSupported('video/webm;codecs=vp8,opus') ||
    isTypeSupported('video/webm;codecs=h264,opus') ||
    isTypeSupported('video/webm')
  );
}

// Audio always works via the legacy path (AAC on Safari, Opus everywhere
// else). Exposed for parity; callers don't need to guard audio.
export function canRecordAudio() {
  if (typeof MediaRecorder === 'undefined') return false;
  return (
    isTypeSupported('audio/webm;codecs=opus') ||
    isTypeSupported('audio/webm') ||
    isTypeSupported('audio/mp4;codecs=mp4a.40.2') ||
    isTypeSupported('audio/mp4') ||
    isTypeSupported('audio/ogg;codecs=opus')
  );
}
